import { apiKeyService } from './apiKeyService';

// Module-level org ID — set by the app when org/portfolio config is resolved
let _resolvedOrgId: string | undefined;

/** Call this to set the org ID. All subsequent API calls will pass it to the backend for credential lookup. */
export const setOrgId = (orgId: string | undefined) => {
  _resolvedOrgId = orgId;
};

/** @deprecated Use setOrgId instead. Kept for backward compatibility during migration. */
export const setAsanaPat = (_pat: string | undefined) => {
  // No-op — PAT is no longer stored client-side
};

/** @deprecated PAT is no longer stored client-side. Returns undefined. */
export const getAsanaPat = (): string | undefined => undefined;

// Module-level field mappings — set by the app when org/portfolio config is resolved
let _resolvedFieldMappings: Array<{fieldName: string; asanaGid: string}> | undefined;

/** Call this to set field mappings from org/portfolio config. All subsequent API calls will use them. */
export const setFieldMappings = (mappings: Array<{fieldName: string; asanaGid: string}> | undefined) => {
  _resolvedFieldMappings = mappings && mappings.length > 0 ? mappings : undefined;
};

/** Get the currently set field mappings */
export const getFieldMappings = () => _resolvedFieldMappings;

/** Convert field mappings array to a map with GID, fieldType, and writeToAsana for the backend */
const _fieldMappingsToMap = (): Record<string, any> | undefined => {
  if (!_resolvedFieldMappings || _resolvedFieldMappings.length === 0) return undefined;
  return _resolvedFieldMappings.reduce((map, fm) => {
    // Send GID, fieldType, and writeToAsana so backend knows how to process each field
    const entry: any = { gid: fm.asanaGid };
    if ((fm as any).fieldType) entry.fieldType = (fm as any).fieldType;
    // Include writeToAsana flag (default true if not specified)
    entry.writeToAsana = (fm as any).writeToAsana !== false;
    map[fm.fieldName] = entry;
    return map;
  }, {} as Record<string, any>);
};

// Helper function to get authentication headers with API key
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    // Get API key headers from the centralized service
    return await apiKeyService.getApiKeyHeaders();
  } catch (error) {
    console.error('❌ Failed to get API key headers:', error);
    // Return basic headers without API key (will likely fail but allows graceful degradation)
    return {
      'Content-Type': 'application/json',
    };
  }
};

// Comprehensive Asana Task interface with all 18+ custom fields
export interface AsanaTask {
  // Core Asana fields
  gid: string; // Asana's primary identifier
  id: string; // Keep for backward compatibility
  name: string;
  notes: string;
  completed: boolean;
  created_at: string; // ISO timestamp when task was created
  created_by: string;
  assignee: {
    gid: string;
    name: string;
    resource_type: string;
  } | null;
  resource_type: string;
  permalink_url: string;
  
  // Primary task content fields
  priority: 'High' | 'Medium' | 'Low' | 'Unset';
  program: string;
  theme: string;
  problem_statement: string; // Primary field - Problem Statement
  desired_outcome: string; // Primary field - Desired Outcome  
  business_impact: string; // Primary field - Business Impact
  aft_description: string;
  teamAssigned: string;
  annualized_entitlement?: string | number;
  stakeholders?: string;
  urgency?: string;
  submitted_by?: string;
  date_submitted?: string;
  conversation_summary?: string;
  impact_metrics?:string;
  // All new custom fields from comprehensive mapping
  aft?: string; // AFT ticket number (e.g., "AFT-20688")
 
  site_types?: string; // Enum: Storage Distribution Center (SDC), etc.
  impacted_region?: string; // Multi-enum: NA, EU, JP, ROW
  hc_sde?: number | null; // Number: Software engineer headcount
  l7_manager?: string; // Enum: Senior manager assignment
  op_priority?: string; // Enum: OP Priority (renamed from Priority AFT)
  l6_sdm?: string; // People: Team lead assignment
  product_domain?: string; // Multi-enum: Product/domain classification
  
  // Legacy fields for backward compatibility
  stack_rank?: number | null; // Number: Numeric ranking
  in_year_entitlement?: number | null; // Number: In-year financial impact
  portfolio?: string; // Multi-enum: Portfolio classification
  impacted_process_path?: string; // Text: Process path details
  director?: string; // Enum: Director assignment
  
  // Optional fields for AI prioritization and comments
  comments?: string; // Optional field for AI prioritization rationale and other comments
  rationale?: string; // IRIS rationale field for prioritization reasoning
  
  target_year?: string;
  request_type?: string;
  
  // New fields for Stakeholder Roadmap
  status?: string; // Enum: Project status (Not Started, In Progress, etc.)
  launch_date?: string; // Date: Project launch date (YYYY-MM-DD)
  key_deliverables?: string; // Text: Key deliverables description
  op_decision?: string; // Enum: OP Decision
  mission_fit?: string; // Enum: Mission Fit
  l7_sdm?: string; // Enum: L7 SDM/Manager
 
  _customFields?: Record<string, any>;
}


// AsanaTaskData interface for task creation (used by FillyOnIris)
// Only includes the essential 12 fields + title/name for posting to Asana
export interface AsanaTaskData {
  title: string; // Task name/title
  problem_statement: string; // Problem Statement (gid: 1211614894061181)
  business_impact: string; // Business Impact (gid: 1211614894061183)
  annualized_entitlement: string; // Annualized Entitlement (AFT) (gid: 1207163892846980)
  impact_metrics: string; // Entitlement Metrics (gid: 1211614894061187)
  desired_outcome: string; // Desired Outcome (gid: 1211614894061185)
  site_types: string; // Site Types (gid: 1209389680616119)
  theme: string; // Theme (AFT) (gid: 1207163893235866)
  program_name: string; // Program (AFT) (gid: 1207163893039399)
  impacted_region: string; // Impacted Region (AFT) (gid: 1207174268816145)
  team: string; // AFT OB Team (gid: 1203162011357977)
  created_by: string; // Created by (gid: 1211614894061213)
  created_at?: string; // Created at date (YYYY-MM-DD) — set by frontend
  notes: string; // Notes (gid: 1207163893361498),
  target_year:string,
  request_type:string,
   
}

// Asana task creation response
export interface AsanaTaskResponse {
  success: boolean;
  taskId?: string;
  taskUrl?: string;
  error?: string;
}

// Enum field configuration interfaces
export interface EnumFieldOption {
  [optionName: string]: string; // option name -> GID mapping
}

export interface EnumFieldConfig {
  fieldGid: string;
  options: EnumFieldOption;
}

export interface EnumFieldConfigResponse {
  impact_metrics: EnumFieldConfig;
  mission_fit: EnumFieldConfig;
  theme: EnumFieldConfig;
  program_name: EnumFieldConfig;
  op_decision: EnumFieldConfig;
  request_type: EnumFieldConfig;
  target_year: EnumFieldConfig;
  status: EnumFieldConfig;
}

export interface EnumConfigApiResponse {
  success: boolean;
  projectId: string;
  enumConfig: EnumFieldConfigResponse;
  cached: boolean;
  timestamp: number;
  cacheInfo: {
    source: string;
    duration: string;
  };
  error?: string;
}

// Create Asana task (used by FillyOnIris)
export const createAsanaTask = async (
  taskData: AsanaTaskData, 
  projectId?: string, 
  signal?: AbortSignal,
  asanaPat?: string
): Promise<AsanaTaskResponse> => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiUrl}/api/asana/create-task`;
    const headers = await getAuthHeaders();
    
    const body: any = { taskData, projectId, orgId: _resolvedOrgId };
    // Pass org/portfolio field mappings if available
    const fieldMap = _fieldMappingsToMap();
    if (fieldMap) {
      body.fieldMappings = fieldMap;
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal
    });

   

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ Backend API Error:', errorData);
      throw new Error(`Backend API Error: ${response.status} - ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
 
    return {
      success: result.success,
      taskId: result.taskId,
      taskUrl: result.taskUrl
    };
  } catch (error) {
    console.error('❌ ASANA SERVICE: Failed to create task:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Cache management
interface TaskCache {
  tasks: AsanaTask[];
  timestamp: number;
  version: number;
  organizationId?: string;
}

const CACHE_KEY_PREFIX = 'asana_tasks_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
// Bump this whenever the AsanaTask schema gains new fields so stale caches are auto-invalidated.
const CACHE_SCHEMA_VERSION = 2;

const memoryCacheMap = new Map<string, TaskCache>();

// Get organization's Asana project ID from localStorage
const getAsanaProjectId = (organizationId?: string): string | null => {
  if (!organizationId) return null;
  try {
    const orgData = localStorage.getItem('organizationHeadcountData');
    if (!orgData) return null;
    const parsed = JSON.parse(orgData);
    // Try to find organization by ID or by asanaProject field
    const org = parsed.organizations?.find((o: any) => 
      o.id === organizationId || o.asanaProject === organizationId
    );
    return org?.asanaProject || organizationId; // Fallback to organizationId if it might be the project ID itself
  } catch {
    return organizationId; // Fallback to organizationId in case of parsing errors
  }
};

// Get cached tasks from localStorage
const getCachedTasks = (organizationId?: string): TaskCache | null => {
  try {
    const asanaProjectId = getAsanaProjectId(organizationId);
    const cacheKey = `${CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const parsedCache: TaskCache = JSON.parse(cached);
    const now = Date.now();
    
    // Invalidate if schema version changed (new fields added to AsanaTask)
    if (parsedCache.version !== CACHE_SCHEMA_VERSION) {
      console.log(`[AsanaCache] Schema version mismatch (cached=${parsedCache.version}, current=${CACHE_SCHEMA_VERSION}) — clearing stale cache`);
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    // Check if cache is still valid (within cache duration)
    if (now - parsedCache.timestamp > CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return parsedCache;
  } catch (error) {
    console.error('❌ ASANA CACHE ERROR: Failed to parse cached tasks:', error);
    const asanaProjectId = getAsanaProjectId(organizationId);
    const cacheKey = `${CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
    localStorage.removeItem(cacheKey);
    return null;
  }
};

// Save tasks to cache
const setCachedTasks = (tasks: AsanaTask[], organizationId?: string): void => {
  try {
    const asanaProjectId = getAsanaProjectId(organizationId);
    const cache: TaskCache = {
      tasks,
      timestamp: Date.now(),
      version: CACHE_SCHEMA_VERSION,
      organizationId
    };
    
    const cacheKey = `${CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
    localStorage.setItem(cacheKey, JSON.stringify(cache));
    memoryCacheMap.set(cacheKey, cache);
    
    
  } catch (error) {
    console.error('❌ ASANA CACHE ERROR: Failed to cache tasks:', error);
  }
};

// Clear cache (used when sync is requested or after updates)
export const clearAsanaTasksCache = (organizationId?: string): void => {
  const asanaProjectId = getAsanaProjectId(organizationId);
  const cacheKey = `${CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
  localStorage.removeItem(cacheKey);
  memoryCacheMap.delete(cacheKey);
 
};

// Get Asana tasks from backend with caching
export const getAsanaTasks = async (
  forceRefresh: boolean = false, 
  projectId?: string, 
  signal?: AbortSignal
): Promise<AsanaTask[]> => {
  // If force refresh is requested, clear cache first
  if (forceRefresh) {
    clearAsanaTasksCache(projectId);
  }
  
  const asanaProjectId = getAsanaProjectId(projectId);
  const cacheKey = `${CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${projectId || 'default'}`;
  
  // Try memory cache first (fastest)
  if (!forceRefresh) {
    const memoryCache = memoryCacheMap.get(cacheKey);
    if (memoryCache) {
      const now = Date.now();
      if (now - memoryCache.timestamp <= CACHE_DURATION) {
       
        return memoryCache.tasks;
      }
    }
  }
  
  // Try localStorage cache
  if (!forceRefresh) {
    const cached = getCachedTasks(projectId);
    if (cached) {
      memoryCacheMap.set(cacheKey, cached);
      return cached.tasks;
    }
  }
  
  // Fetch fresh data from backend
  try {
    const apiUrl = process.env.REACT_APP_API_URL ;
    let url = projectId 
      ? `${apiUrl}/api/asana/tasks?projectId=${encodeURIComponent(projectId)}`
      : `${apiUrl}/api/asana/tasks`;
    if (_resolvedOrgId) {
      url += `${url.includes('?') ? '&' : '?'}orgId=${encodeURIComponent(_resolvedOrgId)}`;
    }
    
    const headers = await getAuthHeaders();
    // Send field mappings via header instead of URL query param (avoids URL length limits)
    const fieldMap = _fieldMappingsToMap();
    if (fieldMap) {
      headers['X-Field-Mappings'] = JSON.stringify(fieldMap);
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch Asana tasks: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const tasks: AsanaTask[] = data.tasks || [];
    
    // ── Debug: log launch_date values from the raw backend response ──
    const withDate = tasks.filter(t => t.launch_date);
    const withoutDate = tasks.filter(t => !t.launch_date);
    console.group(`[AsanaService] getAsanaTasks — fresh fetch (${tasks.length} tasks)`);
    console.log(`  With launch_date: ${withDate.length} | Without: ${withoutDate.length}`);
    if (withDate.length > 0) {
      console.log('  launch_date samples:', withDate.slice(0, 10).map(t => `"${t.name}" → ${t.launch_date}`));
    } else {
      console.warn('  ⚠️ No tasks have launch_date — check backend parseCustomFields or Asana custom field GID');
      // Log first 3 raw task keys to see what fields are actually returned
      if (tasks.length > 0) {
        console.log('  Raw task keys (first task):', Object.keys(tasks[0]));
        console.log('  First task sample:', JSON.stringify(tasks[0]).slice(0, 500));
      }
    }
    console.groupEnd();
    
    // Cache the fresh data
    setCachedTasks(tasks, projectId);
    
    return tasks;
  } catch (error) {
    console.error('❌ ASANA SERVICE ERROR: Failed to fetch tasks:', error);
    
    // Try to return stale cache as fallback
    const staleCacheAsanaProjectId = getAsanaProjectId(projectId);
    const staleCacheKey = `${CACHE_KEY_PREFIX}_${staleCacheAsanaProjectId || 'default'}_${projectId || 'default'}`;
    const staleCache = localStorage.getItem(staleCacheKey);
    if (staleCache) {
      try {
        const parsedCache: TaskCache = JSON.parse(staleCache);
      
        return parsedCache.tasks;
      } catch (parseError) {
        console.error('❌ ASANA SERVICE ERROR: Failed to parse stale cache:', parseError);
      }
    }
    
    // Final fallback to empty array
     
    return [];
  }
};

// Update task priority (used by TaskPrioritization)
export const updateTaskPriority = async (taskId: string, priority: string): Promise<void> => {
  // TODO: Implement actual Asana API call
 
};

// Bulk update tasks (generic function for multiple fields)
export const bulkUpdateTasks = async (
  updates: Array<{gid: string, fields: Partial<AsanaTask>}>, 
  projectId?: string, 
  signal?: AbortSignal
): Promise<void> => {
  try {
    console.log(`🔄 BULK UPDATE: Processing ${updates.length} tasks`);
    
    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiUrl}/api/asana/bulk-update`;
    const headers = await getAuthHeaders();
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ updates, projectId, orgId: _resolvedOrgId, fieldMappings: _fieldMappingsToMap() }),
      signal
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Failed to bulk update tasks: ${response.status} ${response.statusText} - ${errorText}`);
    }

    console.log(`✅ BULK UPDATE COMPLETE: ${updates.length} tasks processed successfully`);
    
    // Clear cache after successful update so next fetch gets fresh data
    clearAsanaTasksCache(projectId);
    
  } catch (error) {
    console.error('❌ ASANA SERVICE ERROR: Failed to bulk update tasks:', error);
    throw error;
  }
};

// Delete tasks (bulk delete function)
export const deleteTasks = async (
  taskGids: string[], 
  projectId?: string, 
  signal?: AbortSignal
): Promise<AsanaTaskResponse> => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiUrl}/api/asana/delete-tasks`;
    const headers = await getAuthHeaders();
    
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({
        taskGids,
        projectId,
        orgId: _resolvedOrgId,
        fieldMappings: _fieldMappingsToMap()
      }),
      signal
    });

   
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ Backend Delete API Error:', errorData);
      throw new Error(`Backend Delete API Error: ${response.status} - ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    
    
    // Clear cache after successful delete so next fetch gets fresh data
    clearAsanaTasksCache(projectId);
    
    return {
      success: result.success,
      taskId: result.deletedCount?.toString(),
      error: result.error
    };
  } catch (error) {
    console.error('❌ ASANA SERVICE: Failed to delete tasks:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// ENUM FIELD CONFIG CACHING
interface EnumConfigCache {
  enumConfig: EnumFieldConfigResponse;
  timestamp: number;
  version: number;
  organizationId?: string;
}

const ENUM_CACHE_KEY_PREFIX = 'asana_enum_config_cache';
const enumConfigMemoryCache = new Map<string, EnumConfigCache>();

// Get cached enum config from localStorage
const getCachedEnumConfig = (organizationId?: string): EnumConfigCache | null => {
  try {
    const asanaProjectId = getAsanaProjectId(organizationId);
    const cacheKey = `${ENUM_CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const parsedCache: EnumConfigCache = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid (within cache duration)
    if (now - parsedCache.timestamp > CACHE_DURATION) {
    
      localStorage.removeItem(cacheKey);
      return null;
    }
    
   
    
    return parsedCache;
  } catch (error) {
    console.error('❌ ENUM CACHE ERROR: Failed to parse cached enum config:', error);
    const asanaProjectId = getAsanaProjectId(organizationId);
    const cacheKey = `${ENUM_CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
    localStorage.removeItem(cacheKey);
    return null;
  }
};

// Save enum config to cache
const setCachedEnumConfig = (enumConfig: EnumFieldConfigResponse, organizationId?: string): void => {
  try {
    const asanaProjectId = getAsanaProjectId(organizationId);
    const cache: EnumConfigCache = {
      enumConfig,
      timestamp: Date.now(),
      version: 1,
      organizationId
    };
    
    const cacheKey = `${ENUM_CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
    localStorage.setItem(cacheKey, JSON.stringify(cache));
    enumConfigMemoryCache.set(cacheKey, cache);
    
 
  } catch (error) {
    console.error('❌ ENUM CACHE ERROR: Failed to cache enum config:', error);
  }
};

// Clear enum config cache
export const clearEnumConfigCache = (organizationId?: string): void => {
  const asanaProjectId = getAsanaProjectId(organizationId);
  const cacheKey = `${ENUM_CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${organizationId || 'default'}`;
  localStorage.removeItem(cacheKey);
  enumConfigMemoryCache.delete(cacheKey);
   
};

// Get Asana enum field configuration with caching
export const getEnumFieldConfig = async (
  forceRefresh: boolean = false,
  projectId?: string,
  signal?: AbortSignal
): Promise<EnumFieldConfigResponse> => {
  // If force refresh is requested, clear cache first
  if (forceRefresh) {
    clearEnumConfigCache(projectId);
  }
  
  const asanaProjectId = getAsanaProjectId(projectId);
  const cacheKey = `${ENUM_CACHE_KEY_PREFIX}_${asanaProjectId || 'default'}_${projectId || 'default'}`;
  
  // Try memory cache first (fastest)
  if (!forceRefresh) {
    const memoryCache = enumConfigMemoryCache.get(cacheKey);
    if (memoryCache) {
      const now = Date.now();
      if (now - memoryCache.timestamp <= CACHE_DURATION) {
       
        return memoryCache.enumConfig;
      }
    }
  }
  
  // Try localStorage cache
  if (!forceRefresh) {
    const cached = getCachedEnumConfig(projectId);
    if (cached) {
      enumConfigMemoryCache.set(cacheKey, cached);
      return cached.enumConfig;
    }
  }
  
  // Fetch fresh data from backend
  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    let url = projectId 
      ? `${apiUrl}/api/asana/enum-config?projectId=${encodeURIComponent(projectId)}`
      : `${apiUrl}/api/asana/enum-config`;
    if (_resolvedOrgId) {
      url += `${url.includes('?') ? '&' : '?'}orgId=${encodeURIComponent(_resolvedOrgId)}`;
    }
    const fieldMapEnum = _fieldMappingsToMap();
    if (fieldMapEnum) {
      url += `${url.includes('?') ? '&' : '?'}fieldMappings=${encodeURIComponent(JSON.stringify(fieldMapEnum))}`;
    }
    
    const headers = await getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch enum config: ${response.status} ${response.statusText}`);
    }

    const data: EnumConfigApiResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch enum config');
    }
    
 
    
    // Cache the fresh data
    setCachedEnumConfig(data.enumConfig, projectId);
    
    return data.enumConfig;
  } catch (error) {
    console.error('❌ ENUM SERVICE ERROR: Failed to fetch enum config:', error);
    
    // Try to return stale cache as fallback
    const staleCacheAsanaProjectId = getAsanaProjectId(projectId);
    const staleCacheKey = `${ENUM_CACHE_KEY_PREFIX}_${staleCacheAsanaProjectId || 'default'}_${projectId || 'default'}`;
    const staleCache = localStorage.getItem(staleCacheKey);
    if (staleCache) {
      try {
        const parsedCache: EnumConfigCache = JSON.parse(staleCache);
       
        return parsedCache.enumConfig;
      } catch (parseError) {
        console.error('❌ ENUM SERVICE ERROR: Failed to parse stale cache:', parseError);
      }
    }
    
    // Final fallback to empty config
     
    return {
      impact_metrics: { fieldGid: '1211651287165115', options: {} },
      mission_fit: { fieldGid: '1211663511894717', options: {} },
      theme: { fieldGid: '1207163893235866', options: {} },
      program_name: { fieldGid: '1207163893039399', options: {} },
      op_decision: { fieldGid: '1211663513934836', options: {} },
      request_type: { fieldGid: '1211722162573119', options: {} },
      target_year: { fieldGid: '1211722162573120', options: {} },
      status: { fieldGid: '1211722162573121', options: {} }
    };
  }
};

// STORY/COMMENT INTERFACES AND FUNCTIONS

// Interface for Asana story/comment
export interface AsanaStory {
  gid: string;
  text: string;
  created_at: string;
  created_by: {
    name: string;
    email: string;
  };
  is_pinned: boolean;
  type: string;
}

// Interface for story API response
export interface AsanaStoryResponse {
  success: boolean;
  stories?: AsanaStory[];
  story?: AsanaStory;
  count?: number;
  error?: string;
}

// Get task stories/comments from backend
export const getTaskStories = async (
  taskGid: string,
  projectId?: string,
  signal?: AbortSignal
): Promise<AsanaStoryResponse> => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    let url = `${apiUrl}/api/asana/stories?taskGid=${encodeURIComponent(taskGid)}${projectId ? `&projectId=${encodeURIComponent(projectId)}` : ''}`;
    if (_resolvedOrgId) {
      url += `&orgId=${encodeURIComponent(_resolvedOrgId)}`;
    }
    
    const headers = await getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to fetch task stories: ${response.status} - ${errorData.error || response.statusText}`);
    }

    const data = await response.json();
    
    return {
      success: data.success,
      stories: data.stories || [],
      count: data.count || 0
    };
  } catch (error) {
    console.error('❌ ASANA SERVICE ERROR: Failed to fetch task stories:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Add story/comment to task
export const addTaskStory = async (
  taskGid: string,
  text: string,
  projectId?: string,
  isPinned: boolean = false,
  userInfo?: { alias: string; email: string },
  signal?: AbortSignal
): Promise<AsanaStoryResponse> => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiUrl}/api/asana/stories`;
    const headers = await getAuthHeaders();
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        taskGid,
        text,
        isPinned,
        projectId,
        orgId: _resolvedOrgId,
        userInfo
      }),
      signal
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to add task story: ${response.status} - ${errorData.error || response.statusText}`);
    }

    const data = await response.json();
    
    return {
      success: data.success,
      story: data.story
    };
  } catch (error) {
    console.error('❌ ASANA SERVICE ERROR: Failed to add task story:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};
